import {
  CourtRoom,
  CourtSession,
  RoomMember,
  PaymentRecord,
  MemberBalance,
  Settlement,
  SessionInput,
  PaymentInput,
  CreateRoomInput,
  PendingWrite,
  SyncResult,
  RoomEventCallbacks,
  Currency,
} from '../types';
import { CourtSplitterCache } from './CourtSplitterCache';
import { BalanceCalculator } from './BalanceCalculator';

const API_BASE = process.env.REACT_APP_COURT_SPLITTER_API || '';

/**
 * CourtSplitterService — Cloud-backed service with offline fallback.
 *
 * Strategy:
 * - Reads: serve from local cache first, background-refresh from API
 * - Writes: optimistic local update + queue API call (or direct if online)
 * - Sync: flush pending writes on reconnect
 */
export class CourtSplitterService {
  private cache: CourtSplitterCache;
  private authToken: string | null = null;
  private userId: string | null = null;
  private userName: string | null = null;
  private syncInProgress = false;

  constructor() {
    this.cache = new CourtSplitterCache();
    // Migrate legacy data if present
    this.cache.migrateFromLegacy();
  }

  // ============================================================================
  // Auth Configuration
  // ============================================================================

  /** Set the current user's auth token for API calls */
  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  /** Set the current user identity */
  setUser(userId: string, userName: string): void {
    this.userId = userId;
    this.userName = userName;
  }

  // ============================================================================
  // Room Operations
  // ============================================================================

  /** Create a new room */
  async createRoom(input: CreateRoomInput): Promise<CourtRoom> {
    const roomCode = this.generateRoomCode();
    const now = Date.now();

    const room: CourtRoom = {
      id: roomCode,
      name: input.name.trim(),
      createdBy: this.userId || 'anonymous',
      createdAt: now,
      updatedAt: now,
      currency: input.currency,
      members: [
        {
          id: this.userId || 'anonymous',
          name: this.userName || 'You',
          joinedAt: now,
          isAdmin: true,
          isLinked: true,
        },
      ],
      sessions: [],
      payments: [],
    };

    // Optimistic: add to cache immediately
    this.cache.upsertRoom(room);

    // Queue API call
    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        const serverRoom = await this.apiPost<CourtRoom>('/court-splitter/rooms', input);
        this.cache.upsertRoom(serverRoom);
        return serverRoom;
      } catch (e) {
        // Offline fallback — queue for later
        this.cache.addPendingWrite('createRoom', roomCode, { ...input, roomCode });
        console.warn('[CourtSplitterService] Create room queued for sync:', e);
      }
    } else {
      this.cache.addPendingWrite('createRoom', roomCode, { ...input, roomCode });
    }

    return room;
  }

  /** Get a room by code (cache-first, background refresh) */
  async getRoom(roomCode: string): Promise<CourtRoom | null> {
    // Serve from cache immediately
    const cached = this.cache.getRoom(roomCode);

    // Background refresh if online
    if (CourtSplitterCache.isOnline() && API_BASE) {
      this.backgroundRefreshRoom(roomCode);
    }

    return cached;
  }

  /** Get all rooms the current user belongs to */
  async getMyRooms(): Promise<CourtRoom[]> {
    const userId = this.userId;
    if (!userId) return [];

    // Serve from cache
    const cached = this.cache.getUserRooms(userId);

    // Background refresh
    if (CourtSplitterCache.isOnline() && API_BASE) {
      this.backgroundRefreshMyRooms();
    }

    return cached;
  }

  /** Join a room by code */
  async joinRoom(roomCode: string): Promise<CourtRoom | null> {
    const room = this.cache.getRoom(roomCode);
    if (!room) {
      // Try to fetch from API
      if (CourtSplitterCache.isOnline() && API_BASE) {
        try {
          const serverRoom = await this.apiPost<CourtRoom>(
            `/court-splitter/rooms/${roomCode}/join`,
            {}
          );
          this.cache.upsertRoom(serverRoom);
          return serverRoom;
        } catch {
          return null;
        }
      }
      return null;
    }

    // Check if already a member
    if (room.members.some(m => m.id === this.userId)) {
      return room;
    }

    // Optimistic update
    const newMember: RoomMember = {
      id: this.userId || 'anonymous',
      name: this.userName || 'You',
      joinedAt: Date.now(),
      isAdmin: false,
      isLinked: true,
    };

    this.cache.applyOptimisticUpdate(roomCode, r => ({
      ...r,
      members: [...r.members, newMember],
    }));

    // Queue API call
    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        await this.apiPost(`/court-splitter/rooms/${roomCode}/join`, {});
      } catch {
        this.cache.addPendingWrite('joinRoom', roomCode, {});
      }
    } else {
      this.cache.addPendingWrite('joinRoom', roomCode, {});
    }

    return this.cache.getRoom(roomCode);
  }

  /** Delete a room (admin only) */
  async deleteRoom(roomCode: string): Promise<void> {
    this.cache.removeRoom(roomCode);

    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        await this.apiDelete(`/court-splitter/rooms/${roomCode}`);
      } catch {
        this.cache.addPendingWrite('deleteRoom', roomCode, {});
      }
    } else {
      this.cache.addPendingWrite('deleteRoom', roomCode, {});
    }
  }

  // ============================================================================
  // Session Operations
  // ============================================================================

  /** Add a session to a room */
  async addSession(roomCode: string, input: SessionInput): Promise<CourtSession> {
    const sessionId = this.generateId();
    const now = Date.now();

    const session: CourtSession = {
      id: sessionId,
      date: input.date,
      totalCost: input.totalCost,
      paidBy: input.paidBy,
      attendees: input.attendees,
      note: input.note,
      createdBy: this.userId || 'anonymous',
      createdAt: now,
    };

    // Optimistic update
    this.cache.applyOptimisticUpdate(roomCode, r => ({
      ...r,
      sessions: [...r.sessions, session],
    }));

    // API call
    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        const serverSession = await this.apiPost<CourtSession>(
          `/court-splitter/rooms/${roomCode}/sessions`,
          input
        );
        // Update with server-assigned ID if different
        if (serverSession.id !== sessionId) {
          this.cache.applyOptimisticUpdate(roomCode, r => ({
            ...r,
            sessions: r.sessions.map(s => s.id === sessionId ? serverSession : s),
          }));
        }
        return serverSession;
      } catch {
        this.cache.addPendingWrite('addSession', roomCode, { ...input, sessionId });
      }
    } else {
      this.cache.addPendingWrite('addSession', roomCode, { ...input, sessionId });
    }

    return session;
  }

  /** Edit an existing session */
  async editSession(roomCode: string, sessionId: string, updates: Partial<SessionInput>): Promise<CourtSession | null> {
    // Optimistic update
    let updatedSession: CourtSession | null = null;

    this.cache.applyOptimisticUpdate(roomCode, r => ({
      ...r,
      sessions: r.sessions.map(s => {
        if (s.id === sessionId) {
          updatedSession = { ...s, ...updates };
          return updatedSession;
        }
        return s;
      }),
    }));

    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        const serverSession = await this.apiPut<CourtSession>(
          `/court-splitter/rooms/${roomCode}/sessions/${sessionId}`,
          updates
        );
        return serverSession;
      } catch {
        this.cache.addPendingWrite('editSession', roomCode, { sessionId, updates });
      }
    } else {
      this.cache.addPendingWrite('editSession', roomCode, { sessionId, updates });
    }

    return updatedSession;
  }

  /** Delete a session */
  async deleteSession(roomCode: string, sessionId: string): Promise<void> {
    // Optimistic update
    this.cache.applyOptimisticUpdate(roomCode, r => ({
      ...r,
      sessions: r.sessions.filter(s => s.id !== sessionId),
    }));

    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        await this.apiDelete(`/court-splitter/rooms/${roomCode}/sessions/${sessionId}`);
      } catch {
        this.cache.addPendingWrite('deleteSession', roomCode, { sessionId });
      }
    } else {
      this.cache.addPendingWrite('deleteSession', roomCode, { sessionId });
    }
  }

  // ============================================================================
  // Member Operations
  // ============================================================================

  /** Add a manual (non-linked) member */
  async addMember(roomCode: string, name: string): Promise<RoomMember> {
    const memberId = this.generateId();
    const member: RoomMember = {
      id: memberId,
      name: name.trim(),
      joinedAt: Date.now(),
      isAdmin: false,
      isLinked: false,
    };

    // Optimistic update
    this.cache.applyOptimisticUpdate(roomCode, r => ({
      ...r,
      members: [...r.members, member],
    }));

    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        const serverMember = await this.apiPost<RoomMember>(
          `/court-splitter/rooms/${roomCode}/members`,
          { name }
        );
        // Update with server-assigned ID
        if (serverMember.id !== memberId) {
          this.cache.applyOptimisticUpdate(roomCode, r => ({
            ...r,
            members: r.members.map(m => m.id === memberId ? serverMember : m),
          }));
        }
        return serverMember;
      } catch {
        this.cache.addPendingWrite('addMember', roomCode, { name, memberId });
      }
    } else {
      this.cache.addPendingWrite('addMember', roomCode, { name, memberId });
    }

    return member;
  }

  /** Remove a member from a room */
  async removeMember(roomCode: string, memberId: string): Promise<void> {
    // Optimistic update — remove from members but keep session history
    this.cache.applyOptimisticUpdate(roomCode, r => ({
      ...r,
      members: r.members.filter(m => m.id !== memberId),
    }));

    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        await this.apiDelete(`/court-splitter/rooms/${roomCode}/members/${memberId}`);
      } catch {
        this.cache.addPendingWrite('removeMember', roomCode, { memberId });
      }
    } else {
      this.cache.addPendingWrite('removeMember', roomCode, { memberId });
    }
  }

  // ============================================================================
  // Payment Operations
  // ============================================================================

  /** Record a settlement payment */
  async recordPayment(roomCode: string, input: PaymentInput): Promise<PaymentRecord> {
    const paymentId = this.generateId();
    const payment: PaymentRecord = {
      id: paymentId,
      fromMemberId: input.fromMemberId,
      toMemberId: input.toMemberId,
      amount: input.amount,
      date: input.date,
      note: input.note,
      createdBy: this.userId || 'anonymous',
      createdAt: Date.now(),
    };

    // Optimistic update
    this.cache.applyOptimisticUpdate(roomCode, r => ({
      ...r,
      payments: [...(r.payments || []), payment],
    }));

    if (CourtSplitterCache.isOnline() && API_BASE) {
      try {
        const serverPayment = await this.apiPost<PaymentRecord>(
          `/court-splitter/rooms/${roomCode}/payments`,
          input
        );
        return serverPayment;
      } catch {
        this.cache.addPendingWrite('addPayment', roomCode, { ...input, paymentId });
      }
    } else {
      this.cache.addPendingWrite('addPayment', roomCode, { ...input, paymentId });
    }

    return payment;
  }

  // ============================================================================
  // Balance Calculations (local, no network)
  // ============================================================================

  /** Calculate balances for a room */
  getBalances(roomCode: string): MemberBalance[] {
    const room = this.cache.getRoom(roomCode);
    if (!room) return [];
    return BalanceCalculator.calculateBalances(room);
  }

  /** Calculate settlements for a room */
  getSettlements(roomCode: string): Settlement[] {
    const room = this.cache.getRoom(roomCode);
    if (!room) return [];
    return BalanceCalculator.calculateSettlements(room);
  }

  // ============================================================================
  // Sync
  // ============================================================================

  /** Flush pending writes to the server */
  async syncPendingWrites(): Promise<SyncResult> {
    if (this.syncInProgress) {
      return { success: false, synced: 0, failed: 0, errors: ['Sync already in progress'] };
    }
    if (!CourtSplitterCache.isOnline() || !API_BASE) {
      return { success: false, synced: 0, failed: 0, errors: ['Offline'] };
    }

    this.syncInProgress = true;
    const writes = this.cache.getPendingWrites();
    let synced = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const write of writes) {
      try {
        await this.executePendingWrite(write);
        this.cache.removePendingWrite(write.id);
        synced++;
      } catch (e) {
        this.cache.incrementRetryCount(write.id);
        failed++;
        errors.push(`${write.type}@${write.roomCode}: ${e instanceof Error ? e.message : 'Unknown error'}`);
      }
    }

    // Prune writes that exceeded max retries
    const pruned = this.cache.pruneFailedWrites();
    if (pruned.length > 0) {
      errors.push(`${pruned.length} write(s) permanently failed after max retries`);
    }

    this.cache.updateSyncTimestamp();
    this.syncInProgress = false;

    return { success: failed === 0, synced, failed, errors };
  }

  /** Get pending write count */
  getPendingWriteCount(): number {
    return this.cache.getPendingWriteCount();
  }

  // ============================================================================
  // Real-time (placeholder — implemented in CourtSplitterSync)
  // ============================================================================

  /** Subscribe to room events (delegated to sync service) */
  subscribeToRoom(_roomCode: string, _callbacks: RoomEventCallbacks): void {
    // Implemented by CourtSplitterSync service
  }

  /** Unsubscribe from room events */
  unsubscribeFromRoom(_roomCode: string): void {
    // Implemented by CourtSplitterSync service
  }

  // ============================================================================
  // Demo Data
  // ============================================================================

  /** Load synthetic demo data for testing */
  loadDemoData(): CourtRoom {
    const roomCode = this.generateRoomCode();
    const now = Date.now();

    const members: RoomMember[] = [
      { id: 'demo-ravi', name: 'Ravi', joinedAt: now - 90 * 86400000, isAdmin: true, isLinked: false },
      { id: 'demo-priya', name: 'Priya', joinedAt: now - 90 * 86400000, isAdmin: false, isLinked: false },
      { id: 'demo-amit', name: 'Amit', joinedAt: now - 90 * 86400000, isAdmin: false, isLinked: false },
      { id: 'demo-sneha', name: 'Sneha', joinedAt: now - 85 * 86400000, isAdmin: false, isLinked: false },
      { id: 'demo-vikram', name: 'Vikram', joinedAt: now - 80 * 86400000, isAdmin: false, isLinked: false },
    ];

    // Generate 12 Tuesdays going back from today
    const sessions: CourtSession[] = [];
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilTuesday = dayOfWeek >= 2 ? dayOfWeek - 2 : dayOfWeek + 5;
    const lastTuesday = new Date(today);
    lastTuesday.setDate(today.getDate() - daysUntilTuesday);

    const costs = [20, 18, 22, 15, 25, 20, 18, 22, 20, 15, 25, 20];
    const payers = [
      'demo-ravi', 'demo-amit', 'demo-ravi', 'demo-priya',
      'demo-amit', 'demo-ravi', 'demo-vikram', 'demo-amit',
      'demo-ravi', 'demo-sneha', 'demo-amit', 'demo-ravi',
    ];
    const attendeePatterns = [
      ['demo-ravi', 'demo-priya', 'demo-amit', 'demo-sneha', 'demo-vikram'],
      ['demo-ravi', 'demo-priya', 'demo-amit', 'demo-vikram'],
      ['demo-ravi', 'demo-priya', 'demo-amit', 'demo-sneha', 'demo-vikram'],
      ['demo-ravi', 'demo-amit', 'demo-sneha', 'demo-vikram'],
      ['demo-ravi', 'demo-priya', 'demo-amit', 'demo-sneha', 'demo-vikram'],
      ['demo-priya', 'demo-amit', 'demo-sneha', 'demo-vikram'],
      ['demo-ravi', 'demo-priya', 'demo-amit', 'demo-sneha'],
      ['demo-ravi', 'demo-priya', 'demo-amit', 'demo-sneha', 'demo-vikram'],
      ['demo-ravi', 'demo-amit', 'demo-vikram'],
      ['demo-ravi', 'demo-priya', 'demo-amit', 'demo-sneha', 'demo-vikram'],
      ['demo-ravi', 'demo-priya', 'demo-sneha', 'demo-vikram'],
      ['demo-ravi', 'demo-priya', 'demo-amit', 'demo-sneha', 'demo-vikram'],
    ];
    const notes: (string | undefined)[] = [
      'Court 3, 2 hours', undefined, 'Booked extra hour', undefined,
      'Tournament prep', undefined, 'Court 1', undefined,
      'Short session', 'Court 3', undefined, 'End of month session',
    ];

    for (let i = 0; i < 12; i++) {
      const sessionDate = new Date(lastTuesday);
      sessionDate.setDate(lastTuesday.getDate() - i * 7);
      sessions.push({
        id: `demo-session-${i}`,
        date: sessionDate.toISOString().split('T')[0],
        totalCost: costs[i],
        paidBy: payers[i],
        attendees: attendeePatterns[i],
        note: notes[i],
        createdBy: payers[i],
        createdAt: sessionDate.getTime(),
      });
    }

    const room: CourtRoom = {
      id: roomCode,
      name: 'Tuesday Badminton Crew',
      createdBy: 'demo-ravi',
      createdAt: now - 90 * 86400000,
      updatedAt: now,
      currency: 'USD',
      members,
      sessions,
      payments: [],
    };

    this.cache.upsertRoom(room);
    return room;
  }

  // ============================================================================
  // Lookup helpers (for component use)
  // ============================================================================

  /** Get room from cache synchronously (no network) */
  getRoomSync(roomCode: string): CourtRoom | null {
    return this.cache.getRoom(roomCode);
  }

  /** Get all rooms from cache synchronously */
  getRoomsSync(): CourtRoom[] {
    return this.cache.getRooms();
  }

  /** Get rooms for current user from cache */
  getMyRoomsSync(): CourtRoom[] {
    if (!this.userId) return this.cache.getRooms();
    return this.cache.getUserRooms(this.userId);
  }

  /** Find a room by code (case-insensitive) */
  findRoomByCode(code: string): CourtRoom | null {
    const normalized = code.toUpperCase().trim();
    return this.cache.getRooms().find(r => r.id.toUpperCase() === normalized) || null;
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  private generateRoomCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const existingCodes = new Set(this.cache.getRooms().map(r => r.id));
    let code: string;
    do {
      code = '';
      for (let i = 0; i < 6; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
    } while (existingCodes.has(code));
    return code;
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  private async executePendingWrite(write: PendingWrite): Promise<void> {
    switch (write.type) {
      case 'createRoom':
        await this.apiPost('/court-splitter/rooms', write.payload);
        break;
      case 'joinRoom':
        await this.apiPost(`/court-splitter/rooms/${write.roomCode}/join`, {});
        break;
      case 'addSession':
        await this.apiPost(`/court-splitter/rooms/${write.roomCode}/sessions`, write.payload);
        break;
      case 'editSession':
        await this.apiPut(
          `/court-splitter/rooms/${write.roomCode}/sessions/${write.payload.sessionId}`,
          write.payload.updates
        );
        break;
      case 'deleteSession':
        await this.apiDelete(
          `/court-splitter/rooms/${write.roomCode}/sessions/${write.payload.sessionId}`
        );
        break;
      case 'addMember':
        await this.apiPost(`/court-splitter/rooms/${write.roomCode}/members`, {
          name: write.payload.name,
        });
        break;
      case 'removeMember':
        await this.apiDelete(
          `/court-splitter/rooms/${write.roomCode}/members/${write.payload.memberId}`
        );
        break;
      case 'addPayment':
        await this.apiPost(`/court-splitter/rooms/${write.roomCode}/payments`, write.payload);
        break;
      case 'deleteRoom':
        await this.apiDelete(`/court-splitter/rooms/${write.roomCode}`);
        break;
    }
  }

  private async backgroundRefreshRoom(roomCode: string): Promise<void> {
    try {
      const room = await this.apiGet<CourtRoom>(`/court-splitter/rooms/${roomCode}`);
      if (room) {
        this.cache.upsertRoom(room);
      }
    } catch {
      // Silent failure for background refresh
    }
  }

  private async backgroundRefreshMyRooms(): Promise<void> {
    try {
      const rooms = await this.apiGet<CourtRoom[]>('/court-splitter/my-rooms');
      if (rooms && Array.isArray(rooms)) {
        this.cache.setRooms(rooms);
      }
    } catch {
      // Silent failure for background refresh
    }
  }

  // ============================================================================
  // HTTP Client
  // ============================================================================

  private async apiGet<T>(path: string): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  private async apiPost<T>(path: string, body: any): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  private async apiPut<T>(path: string, body: any): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  private async apiDelete(path: string): Promise<void> {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    return headers;
  }
}
